import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { db, formatDate, type SubscriptionRecord } from "@/lib/billing";

type Subscriber = SubscriptionRecord & {
  profile?: { full_name: string | null; phone_number: string | null } | null;
  usage_counters?: Array<{ articles_created: number; websites_created: number; ai_credits_used: number }>;
};

async function fetchSubscribers(): Promise<Subscriber[]> {
  const { data: subscriptions, error } = await db
    .from("user_subscriptions")
    .select("*, packages(*)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  const userIds = Array.from(new Set((subscriptions ?? []).map((s: SubscriptionRecord) => s.user_id)));
  const { data: profiles, error: profileError } = userIds.length
    ? await db.from("profiles").select("id, full_name, phone_number").in("id", userIds)
    : { data: [], error: null };
  if (profileError) throw profileError;
  const profileMap = new Map((profiles ?? []).map((profile: any) => [profile.id, profile]));
  return (subscriptions ?? []).map((subscription: SubscriptionRecord) => ({
    ...subscription,
    profile: profileMap.get(subscription.user_id) ?? null,
  }));
}

export default function AdminSubscribersPage() {
  const [search, setSearch] = useState("");
  const { data: subscribers = [], isLoading } = useQuery({ queryKey: ["admin-subscribers"], queryFn: fetchSubscribers });
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return subscribers.filter((s) => [s.user_id, s.status, s.packages?.name, s.profile?.full_name].join(" ").toLowerCase().includes(q));
  }, [subscribers, search]);

  const exportCsv = () => {
    const rows = filtered.map((s) => ({
      user_id: s.user_id,
      name: s.profile?.full_name ?? "",
      package: s.packages?.name ?? "",
      status: s.status,
      billing_cycle: s.billing_cycle ?? "",
      renewal: s.current_period_end ?? s.trial_end ?? "",
      latest_invoice_id: s.latest_invoice_id ?? "",
      stripe_customer_id: s.stripe_customer_id ?? "",
    }));
    const csv = [Object.keys(rows[0] ?? {}).join(","), ...rows.map((r) => Object.values(r).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "delapresse-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Subscribers</h1>
          <p className="text-xs text-muted-foreground">Subscription status, trial state, package, renewal, and Stripe ownership.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
      </header>
      <main className="p-6">
        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">Subscriber accounts</CardTitle>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search subscribers..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Subscriber</TableHead><TableHead>Package</TableHead><TableHead>Status</TableHead><TableHead>Cycle</TableHead><TableHead>Renewal / Trial end</TableHead><TableHead>Latest invoice</TableHead><TableHead>Stripe customer</TableHead><TableHead>Overrides</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filtered.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <div className="font-medium">{sub.profile?.full_name ?? "Unnamed user"}</div>
                        <div className="max-w-[220px] truncate text-xs text-muted-foreground">{sub.user_id}</div>
                      </TableCell>
                      <TableCell>{sub.packages?.name ?? "Unknown"}</TableCell>
                      <TableCell><StatusBadge status={sub.status} /></TableCell>
                      <TableCell>{sub.billing_cycle ?? "monthly"}</TableCell>
                      <TableCell>{formatDate(sub.current_period_end ?? sub.trial_end)}</TableCell>
                      <TableCell className="max-w-[140px] truncate text-xs text-muted-foreground">{sub.latest_invoice_id ?? "None"}</TableCell>
                      <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">{sub.stripe_customer_id ?? "Not created"}</TableCell>
                      <TableCell>{Object.keys(sub.manual_limit_overrides ?? {}).length ? "Custom" : "None"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
